import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {Bar} from "react-chartjs-2"
import {Chart, ArcElement, Tooltip, Legend, Colors} from "chart.js/auto"
import {useHistory} from "react-router-dom"
import {Container} from "../../../components/Container"
import {useRPC} from "../../../hooks/rpc"
import {EnergyModel} from "../../../store/control/state"
import {onClickHandler} from "../../../utils/onclick"
import {PhaseField} from "../components/PhaseField"
import {barDatas, barOptions} from "../settings/options"
import {calculateWeekDay, sum} from "../utils/calculate"
import useStaticData from "../data/static"
import timeConverter from "../utils/time"
import {useDispatch, useSelector} from "react-redux"
import {backButtonToggle} from "../../../store/ui/actions"
import {PhaseModel} from "../types/types"
import {useLocalization} from "../../../hooks/localization"
import "./index.scss"
import useChartLoader from "../hooks/chart-loader"

Chart.register(ArcElement, Tooltip, Legend, Colors)
Chart.defaults.color = "#fff"
Chart.defaults.borderColor = "rgb(136, 136, 136)"
Chart.defaults.font.size = 8

const BACK_URL = "/energy"
const date = new Date()
export const EnergyConsumptionAmount = memo(function EnergyConsumptionAmount() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const [barChartLabel, setBarChartLabel] = useState("hour")
    const [energyHourly, setEnergyHourly] = useState<EnergyModel[]>([])
    const [energyDaily, setEnergyDaily] = useState<EnergyModel[]>([])
    const [energyMonthly, setEnergyMonthly] = useState<EnergyModel[]>([])
    const controls = useSelector(state => state.control.controlList)
    const energyControlList = useSelector(state => state.control.energyState)
    const controlList = controls.filter(control => control.type_id === "200")
    const history = useHistory()
    const dispatch = useDispatch()
    const {isLoaded, loading} = useChartLoader()

    const {hours, days, months} = useStaticData()
    dispatch(backButtonToggle({is_open: true, to: "/energy"}))
    const maxValue = Number(energyControlList.max_point)

    const returnedFilterMonths: number[] = useMemo(() => {
        const data = []
        const findValue = (month: number) =>
            energyMonthly.filter(value => new Date(value?.month).getMonth() + 1 === month).find(total => total.total)
                ?.total
        for (let j = 1; j <= 12; j++) {
            data.push({
                month: j,
                total: findValue(j) || 0
            })
        }
        return data.map(value => value?.total)
    }, [energyMonthly])

    const returnedFilterDays: number[] = useMemo(() => {
        const monthDayCount = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
        const getAllDate = (day: number) => new Date(date.getFullYear(), date.getMonth(), day).getDay()
        const data = []
        const findValue = (day: number) =>
            energyDaily.filter(value => new Date(value?.date).getDate() === day).find(total => total.total)?.total

        const pastDays = monthDayCount - date.getDate()
        const followingDays = monthDayCount - pastDays
        for (let j = 1; j <= followingDays; j++) {
            data.push({
                week: getAllDate(j),
                day: j,
                total: findValue(j) || 0
            })
        }

        return data.map(value => value.total).slice(calculateWeekDay())
    }, [energyDaily])

    const returnedFilterHours: number[] = useMemo(() => {
        const today = date.getDate()
        const findValue = (hour: number) =>
            energyHourly
                .filter(
                    value =>
                        Number(timeConverter(value?.time).day) === today &&
                        Number(timeConverter(value?.time).hour) === hour
                )
                .map(value => value.value)
                .find(total => total)
        const data = []
        for (let j = 0; j <= 23; j++) {
            data.push({
                value: findValue(j) || 0,
                time: j
            })
        }
        return data.map(data => data?.value)
    }, [energyHourly])

    const barChartLabels: string[] = useMemo(() => {
        if (barChartLabel === "hour") {
            return hours
        } else if (barChartLabel === "day") {
            return days
        } else if (barChartLabel === "month") {
            return months
        }
    }, [barChartLabel, hours, days, months])
    const barChartData: number[] = useMemo(() => {
        if (barChartLabel === "hour") {
            return returnedFilterHours
        } else if (barChartLabel === "day") {
            return returnedFilterDays
        } else if (barChartLabel === "month") {
            return returnedFilterMonths
        }
    }, [barChartLabel, returnedFilterHours, returnedFilterMonths, returnedFilterDays])

    const barChartDatas = barDatas(barChartLabels, barChartData, ["rgb(7, 255, 40)"])
    const [amountValue, setAmountValue] = useState(barChartData.reduce(sum, 0) || 0)

    const Phases: PhaseModel[] = useMemo(() => controlList, [controlList])
    const [selectedPhase, setSelectedPhase] = useState({
        id: Phases[0]?.id || undefined,
        selected: true
    })
    const onClickRequestEnergyMonitor = useCallback(() => {
        rpc.GetEnergyMonitorHourly({control_id: selectedPhase?.id}).then(data => setEnergyHourly(data || []))
        rpc.GetEnergyMonitorDaily({control_id: selectedPhase?.id}).then(data => setEnergyDaily(data || []))
        rpc.GetEnergyMonitorMonthly({control_id: selectedPhase?.id}).then(data => setEnergyMonthly(data || []))
    }, [rpc, selectedPhase])

    const onClickAmountStatus = useCallback(
        (action: number) => {
            let newChartLabel = barChartLabel
            let newChartAmount = amountValue
            if (action === 1) {
                newChartLabel = "hour"
            } else if (action === 2) {
                newChartLabel = "day"
            } else if (action === 3) {
                newChartLabel = "month"
            }
            setBarChartLabel(newChartLabel)
            if (newChartLabel === "hour") {
                newChartAmount = barChartData.reduce(sum, 0)
            } else if (newChartLabel === "day") {
                newChartAmount = barChartData.reduce(sum, 0)
            } else if (newChartLabel === "month") {
                newChartAmount = barChartData.reduce(sum, 0)
            }
            setAmountValue(newChartAmount)
            onClickRequestEnergyMonitor()
        },
        [barChartLabel, amountValue, barChartData, onClickRequestEnergyMonitor]
    )
    useEffect(() => setAmountValue(barChartData.reduce(sum, 0)), [barChartData])

    const onClickPhaseChanging = useCallback(
        (id: string) => {
            Phases.find(
                phase =>
                    phase.id === id &&
                    setSelectedPhase({
                        id: id,
                        selected: true
                    })
            )
            onClickRequestEnergyMonitor()
        },
        [Phases, onClickRequestEnergyMonitor]
    )

    useEffect(() => {
        onClickRequestEnergyMonitor()
    }, [onClickRequestEnergyMonitor])

    return (
        <Container>
            <div className="page-wrapper">
                <div className="energy-wrapper">
                    <div className="energy-page-container">
                        <PhaseField
                            selectedPhase={selectedPhase}
                            //onClickAllPhase={onClickAllPhase}
                            onClickPhaseChanging={onClickPhaseChanging}
                        />
                    </div>
                    <div className="energy-page-full-screen-container">
                        <div className="energy-page-full-screen">
                            {loading && (
                                <div className="chart-spin-container">
                                    <div className="chart-spin" />
                                </div>
                            )}
                            <span className="energy-top-action" style={isLoaded}>
                                <span className="energy-top-action-text">
                                    {t("energy_page.amount_chart")} &nbsp;<small>(kW/h)</small>
                                </span>
                                <span className="energy-top-action-button">
                                    <button
                                        {...onClickHandler(() => onClickAmountStatus(1))}
                                        className={`${barChartLabel === "hour" ? "energy-active-button" : ""}`}
                                    >
                                        {t("energy_page.hour")}
                                    </button>
                                    <button
                                        {...onClickHandler(() => onClickAmountStatus(2))}
                                        className={`${barChartLabel === "day" ? "energy-active-button" : ""}`}
                                    >
                                        {t("energy_page.day")}
                                    </button>
                                    <button
                                        {...onClickHandler(() => onClickAmountStatus(3))}
                                        className={`${barChartLabel === "month" ? "energy-active-button" : ""}`}
                                    >
                                        {t("energy_page.month")}
                                    </button>
                                </span>
                            </span>
                            <div
                                className="fullscreen-bar-chart"
                                onDoubleClick={() => history.push(BACK_URL)}
                                style={isLoaded}
                            >
                                <Bar data={barChartDatas} options={barOptions(maxValue)} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    )
})
